import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  Alert,
  Chip,
  IconButton,
  Container,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import logo from "../../imges/logos/mindsinaction/logo.png";

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import primaryBackground from "../../assets/Backgrounds/primaryBg.svg";

const RegistrationSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const invoiceRef = useRef();
  const [open, setOpen] = useState(false);

  const defaultData = {
    studentNumber: 'STU001234',
    reference: 'STU001234',
    amount: '0.00',
    email: 'student@email.com',
    paymentMethod: 'EFT',
    bankDetails: {
      bankName: 'Bank Windhoek',
      accountName: 'MINDSINACTION STEAM CENTRE PTY LTD',
      accountNumber: '8046582717',
      branchCode: '483-872',
    }
  };

  const data = location.state || defaultData;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleGeneratePDF = async () => {
    const canvas = await html2canvas(invoiceRef.current, { scale: 2 });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(`Invoice-${data.studentNumber}.pdf`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundImage: `url(${primaryBackground})`,
        backgroundSize: 'cover',
        p: { xs: 1, sm: 2 }
      }}
    >
      <Container maxWidth="md" sx={{
        marginTop: '10vh'
      }}>
        <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, mt: { xs: 4, sm: 10 } }}>

          {/* HEADER */}
          <Box textAlign="center" mb={4}>
            <CheckCircleIcon sx={{ fontSize: { xs: 50, sm: 70 }, color: 'green' }} />
            <Typography variant="h4">Registration Successful</Typography>
            <Chip label={`Student: ${data.studentNumber}`} color="success" sx={{ mt: 2 }} />
          </Box>

          <Alert icon={<AccessTimeIcon />} sx={{ mb: 3 }}>
            Account activates after payment verification (24–48 hours)
          </Alert>

          {/* PAYMENT SUMMARY */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography display="flex" alignItems="center" flexWrap="wrap">
              Reference: {data.reference}
              <IconButton onClick={() => copy(data.reference)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Typography>
            <Typography color="error">
              Amount: N${data.amount}
            </Typography>
          </Paper>

          {/* ACTIONS */}
          <Box mt={4} display="flex" gap={2} justifyContent="center" flexWrap="wrap">

            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/get-started')}>
              Back
            </Button>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => setOpen(true)}
            >
              Preview Invoice
            </Button>

            <Button startIcon={<HomeIcon />} onClick={() => navigate('/')}>
              Home
            </Button>

            <Button
              startIcon={<LoginIcon />}
              href="https://portal.mindsinaction.com.na"
              target="_blank"
            >
              Portal
            </Button>
          </Box>

          {/* STEPS */}
          <Box mt={5}>
            <Typography variant="h6">What Happens Next?</Typography>

            <List>
              <ListItem>
                1. Make Payment using the provided bank details and correct reference
              </ListItem>
              <ListItem>
                2. Payment will be verified within 24–48 hours
              </ListItem>
              <ListItem>
                3. You will receive login details via email once activated
              </ListItem>
            </List>
          </Box>

          <Alert icon={<EmailIcon />} sx={{ mt: 3 }}>
            Need help? admin@mindsinaction.com.na
          </Alert>

        </Paper>
      </Container>

      {/* INVOICE DIALOG */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {/* 🔥 SCROLL CONTAINER (keeps layout identical on mobile) */}
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              ref={invoiceRef}
              sx={{
                width: '900px', // ✅ forces desktop layout
                p: 5,
                backgroundColor: '#fff',
                color: '#000'
              }}
            >

              {/* HEADER */}
              <Box display="flex" justifyContent="space-between" mb={4}>
                <Box>
                  <img src={logo} alt='logo' style={{ width: '200px', marginBottom: '1rem' }} />
                  <Typography variant="h5" fontWeight="bold">
                    Mindsinaction STEAM Centre
                  </Typography>
                  <Typography variant="body2">Windhoek, Namibia</Typography>
                  <Typography variant="body2">admin@mindsinaction.com.na</Typography>
                </Box>

                <Box textAlign="right">
                  <Typography variant="h6">INVOICE</Typography>
                  <Typography>INV-{data.studentNumber}</Typography>
                  <Typography>Date: {new Date().toLocaleDateString()}</Typography>
                  <Typography>
                    Due: {new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                  </Typography>

                  <Typography display={'flex'} gap={'1rem'}>
                    STATUS:
                    <Typography fontWeight={700} color='#f03800'>
                      PENDING
                    </Typography>
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* CLIENT */}
              <Box mb={3}>
                <Typography variant="subtitle2">Billed To:</Typography>
                <Typography>{data.email}</Typography>
                <Typography>Student No: {data.studentNumber}</Typography>
              </Box>

              {/* TABLE */}
              <Box border="1px solid #ddd" mb={3}>
                <Box display="flex" bgcolor="#f5f5f5" p={1} fontWeight="bold">
                  <Box flex={3}>Description</Box>
                  <Box flex={1}>Payment Type</Box>
                  <Box flex={1}>Qty</Box>
                  <Box flex={1}>Price</Box>
                  <Box flex={1}>Total</Box>
                </Box>

                <Divider />

                <Box display="flex" p={1}>
                  <Box flex={3}>STEAM Programme Registration</Box>
                  <Box flex={1}>{data.paymentMethod}</Box>
                  <Box flex={1}>1</Box>
                  <Box flex={1}>N${data.amount}</Box>
                  <Box flex={1}>N${data.amount}</Box>
                </Box>
              </Box>

              {/* TOTAL */}
              <Box display="flex" justifyContent="flex-end" mb={3}>
                <Box width={250}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography>Total:</Typography>
                    <Typography fontWeight="bold">N${data.amount}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* PAYMENT DETAILS */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Bank Payment Details
                </Typography>

                <Typography>Bank: {data.bankDetails.bankName}</Typography>
                <Typography>Account Name: {data.bankDetails.accountName}</Typography>
                <Typography>Account Number: {data.bankDetails.accountNumber}</Typography>
                <Typography>Branch Code: {data.bankDetails.branchCode}</Typography>
              </Box>

              {/* INSTRUCTIONS */}
              <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fafafa' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Payment Instructions
                </Typography>

                <Typography variant="body2">
                  1. Transfer the exact amount <strong>N${data.amount}</strong> to the bank account above.
                </Typography>

                <Typography variant="body2">
                  2. Use the following reference when making payment:
                </Typography>

                <Typography sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  {data.reference}
                </Typography>

                <Typography variant="body2" sx={{ mt: 1 }}>
                  3. Payments are verified within <strong>24–48 hours</strong>.
                </Typography>

                <Typography variant="body2">
                  4. Your account will be activated after successful verification.
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" color="text.secondary">
                This is a system-generated invoice. Please keep it for your records.
              </Typography>

            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>

          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handleGeneratePDF}
          >
            Print / Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistrationSuccess;